import { Button, Card, Checkbox, Flex, Heading, Text } from '@chakra-ui/react';
import {
	ConfirmModal,
	createDataCollectionContext,
	dbTimeToUserTime,
	i18n,
	parseErrorToString,
	toaster,
	useValidatedSorting,
	type ConfirmModalHandle,
	type DataCollectionContextProps,
	type ItemAction,
} from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useRef, useState, type RefObject } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteMultipleSurveySubmissions } from '../../../api/hooks/useSurveySubmission';
import { useGetSingleSurvey } from '../../../api/hooks/useSurvey';
import { getTranslation } from '../fieldEditor/utils';
import surveySubmissionsService from '../../../api/services/surveySubmissionsService';
import { DateTime } from 'luxon';
import { useAuth } from '../../../context/AuthContext';
import { useDrawer } from '../../../context/DrawerContext';
import { SurveySubmissionDrawer } from './SubmissionDrawer';
import ContactDrawer from '../../contacts/tabs/contacts/drawer/ContactDrawer';

const columnHelper = createColumnHelper<SurveySubmission>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SurveySubmissions() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { openDrawer, closeDrawer } = useDrawer();
	const { id } = useParams();
	const surveyID = id ? parseInt(id, 10) : undefined;

	const { data: survey } = useGetSingleSurvey(surveyID as number, {
		isEnabled: !!surveyID,
		getRelated: 'surveyElements',
		countRelated: 'surveySubmissions',
	});

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader, useDataCollection } = useMemo(
		() => createDataCollectionContext<SurveySubmission>(),
		[],
	);

	function getCheckboxSubmissionValues(element: SurveyElement, submissionValues: SurveySubmissionValue[]) {
		// 0-n answers, so value is number []
		const answers: string[] = [];

		for (let index = 0; index < submissionValues.length; index++) {
			const translation = element.surveyElementOptions.find(option => option.surveyElementOptionID === submissionValues[index]?.surveyElementOptionID)?.translations;

			if (translation) {
				const answer = getTranslation(translation, 'surveyElementOptions.label', i18n.resolvedLanguage as Lang, true);
				answers.push(answer);
			}
		}

		if (answers.length === 0) {
			return '-';
		}
		return answers.join(', ');
	}

	const columns = [
		columnHelper.accessor('surveySubmissionID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contact.contactID', {
			header: t('general.contact'),
			enableSorting: false,
			cell: ({ row }) => {
				return (
					<Text
						width="fit-content"
						borderRadius={'8px'}
						padding={1}
						_hover={{ color: 'white', backgroundColor: 'rgb(61, 213, 152)' }}
						onClick={e => {
							e.stopPropagation();
							openDrawer({
								id: 'contactDrawer',
								content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original?.contactID} />,
							});
						}}
						cursor={'pointer'}
					>
						{`${row.original.contact?.firstName || ''} ${row.original.contact?.lastName || ''} ${row.original.contact?.phone || ''}`}
					</Text>
				);
			},
		}),

		...(survey?.surveyElements
			?.filter(element => element.elementType !== 'pageBreak' && element.elementType !== 'paragraph' && element.elementType !== 'divider')
			.sort((a, b) => {
				const aOrder = a.orderNumber ?? Infinity;
				const bOrder = b.orderNumber ?? Infinity;
				return aOrder - bOrder;
			})
			.map(element => {
				return columnHelper.accessor(row => row.surveySubmissionValues?.map(value => value.surveySubmissionValueID), {
					id: element.surveyElementID.toString(),
					header: getTranslation(element.translations, 'surveyElements.label', i18n.resolvedLanguage as Lang, true),
					enableSorting: false,
					cell: ({ row }) => {
						const value = row.original.surveySubmissionValues.find(surveySubmissionValue => surveySubmissionValue.surveyElementID === element.surveyElementID);

						if (value?.valueText) {
							return (
								<Text maxWidth={'150px'} overflow={'hidden'} textOverflow={'ellipsis'}>
									{value?.valueText}
								</Text>
							);
						}

						if (element.elementType === 'checkbox') {
							return (
								<Text maxWidth={'150px'} overflow={'hidden'} textOverflow={'ellipsis'}>
									{getCheckboxSubmissionValues(
										element,
										row.original.surveySubmissionValues.filter(v => v.surveyElementID === element.surveyElementID),
									)}
								</Text>
							);
						}
					},
				});
			}) || []),

		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return dbTimeToUserTime(row.original.timeCreated, TIMEZONE);
			},
		}),
	];

	useValidatedSorting('surveySubmissions', columns);

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const { mutate: deleteMultipleSurveySubmissions } = useDeleteMultipleSurveySubmissions();

	const selectedItemsActions: Array<ItemAction<SurveySubmission>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					surveySubmissions: ids.map(surveySubmissionID => {
						return {
							surveySubmissionID: parseInt(surveySubmissionID),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_survey_submissions'),
					content: t('general.really_remove_survey_submissions'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleSurveySubmissions(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: [`${survey?.surveyID}-surveySubmissions`] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	const selectColumns = useRef<ConfirmModalHandle>(null);

	function openExportModal(): void {
		if (!survey?.surveyElements) {
			return;
		}
		selectColumns.current?.open({
			title: t('general.export'),
			content: <ExportModal surveyElements={survey.surveyElements} ref={selectColumns} useDataCollection={useDataCollection} />,
			isConfirmButtonVisible: false,
			isBackButtonVisible: false,
		});
	}

	const columnHeaders =
		survey?.surveyElements
			?.filter(element => element.elementType !== 'pageBreak' && element.elementType !== 'paragraph' && element.elementType !== 'divider')
			.sort((a, b) => {
				const aOrder = a.orderNumber ?? Infinity;
				const bOrder = b.orderNumber ?? Infinity;
				return aOrder - bOrder;
			})
			.map(element => {
				return element.surveyElementID.toString();
			}) || [];

	return (
		<Card.Root data-cy="surveySubmissionsCard" maxWidth={'2000px'} width="100%" flex={1} minWidth={'340px'}>
			<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
				<Flex display={'flex'} flexDirection={'column'} gap={3} flex={1} maxWidth={'100%'}>
					<DataCollectionContextProvider
						dataKey="surveySubmissions"
						// No cache for these. fetch always when mounted
						customQueryKey={`${survey?.surveyID}-surveySubmissions`}
						keyAttribute="surveySubmissionID"
						emptyDataText={t('general.no_submissions_found')}
						fetchFn={async (...args) => {
							queryClient.invalidateQueries({ queryKey: ['survey'] });
							return surveySubmissionsService.getSubmissionsBySurveyID(surveyID as number, ...args);
						}}
						queryParams={{
							getRelated: 'surveySubmissionValues,contact',
						}}
						selectedItemsActions={selectedItemsActions}
						initialItemsPerPage={25}
						columns={columns}
						isSelectable={true}
						defaultHiddenColumns={columnHeaders?.slice(5) as string[]}
						isServerSide={true}
						defaultSort={{ id: 'timeCreated', desc: true }}
						onClickRow={row => {
							if (!survey) {
								return;
							}
							openDrawer({
								id: 'submissionDrawer',
								content: (
									<SurveySubmissionDrawer
										survey={survey}
										closeDrawer={() => closeDrawer('submissionDrawer')}
										surveySubmissionID={row.original.surveySubmissionID}
									/>
								),
							});
						}}
					>
						<Flex gap={2} flexWrap={'wrap'}>
							<DataCollectionTableHeader />
							<Button onClick={openExportModal}>{t('general.export')}</Button>
						</Flex>
						<DataCollectionTable />
						<Flex gap={2}>
							<DataCollectionTableFooter />
							<ConfirmModal ref={confirmRemoveRef} />
							<ConfirmModal ref={selectColumns} />
						</Flex>
					</DataCollectionContextProvider>
				</Flex>
			</Card.Body>
		</Card.Root>
	);
}

function ExportModal({
	surveyElements,
	ref,
	useDataCollection,
}: {
	surveyElements: SurveyElement[];
	ref: RefObject<ConfirmModalHandle | null>;
	useDataCollection: () => DataCollectionContextProps<SurveySubmission>;
}) {
	const { t } = useTranslation();
	const { currentAccount } = useAuth();
	const [columns, setColumns] = useState<string[]>([]);

	const rows = useDataCollection().rows;
	const filters = useDataCollection().parsedApiFilters?.filters;
	const relatedFilters = useDataCollection().parsedApiFilters?.relatedFilters;
	const search = useDataCollection().search;

	function getFilters(): string {
		const params: string[] = [];

		if (filters?.length) {
			params.push(`filters=${filters}`);
		}

		if (relatedFilters?.length) {
			params.push(`relatedFilters=${relatedFilters}`);
		}

		if (search?.length) {
			params.push(`search=${search}`);
		}

		return params.length ? `?${params.join('&')}` : '';
	}

	async function handleExport(): Promise<void> {
		try {
			if (!surveyElements[0]?.surveyID) {
				return;
			}

			const response = await surveySubmissionsService.export(surveyElements[0]?.surveyID, {
				filters: getFilters(),
				columnsToExport: columns,
				language: i18n.resolvedLanguage as Lang,
			});

			const now = DateTime.now();
			const formattedNow = now.toFormat('yyyy-MM-dd');
			const filename = `${currentAccount?.name}-survey-submissions-export-${formattedNow}.xlsx`.toLowerCase().replace(/\s+/g, '-');

			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			ref.current?.close();
		} catch (error) {
			toaster.create({
				type: 'error',
				title: parseErrorToString(error),
			});
		}
	}

	const exportableColumns = useMemo(
		() => [
			{ key: 'contacts.firstName', label: t('general.first_name') },
			{ key: 'contacts.lastName', label: t('general.last_name') },
			{ key: 'contacts.email', label: t('general.email') },
			{ key: 'contacts.phone', label: t('general.phone') },
			{ key: 'surveySubmissions.timeCreated', label: t('general.time_created') },

			...(surveyElements
				?.filter(element => element.elementType !== 'pageBreak' && element.elementType !== 'paragraph' && element.elementType !== 'divider')
				.sort((a, b) => {
					const aOrder = a.orderNumber ?? Infinity;
					const bOrder = b.orderNumber ?? Infinity;
					return aOrder - bOrder;
				})
				.map(element => {
					return {
						key: `surveyElements.${element.surveyElementID}`,
						label: getTranslation(element.translations, 'surveyElements.label', i18n.resolvedLanguage as Lang, true),
					};
				}) || []),
		],
		[t, surveyElements],
	);

	return (
		<Flex flexDirection={'column'}>
			<Heading size="sm">{t('general.select_columns_to_export')}</Heading>

			<Flex flexDirection={'column'} gap={2} marginTop={4} marginBottom={2}>
				<Checkbox.Root
					colorPalette="brand.primary"
					checked={columns.length === exportableColumns.length}
					onCheckedChange={({ checked }) => {
						if (checked) {
							setColumns(exportableColumns.map(col => col.key));
						} else {
							setColumns([]);
						}
					}}
				>
					<Checkbox.HiddenInput />
					<Checkbox.Control />
					<Checkbox.Label>{t('general.select_all')}</Checkbox.Label>
				</Checkbox.Root>
				<hr />

				{exportableColumns.map(({ key, label }) => (
					<Checkbox.Root
						key={key}
						colorPalette={'brand.primary'}
						checked={columns.includes(key)}
						onCheckedChange={({ checked }) => {
							if (checked && !columns.includes(key)) {
								setColumns(prev => [...prev, key]);
							} else if (!checked) {
								setColumns(prev => prev.filter(col => col !== key));
							}
						}}
					>
						<Checkbox.HiddenInput />
						<Checkbox.Control id={key} />
						<Checkbox.Label textAlign="left">{label}</Checkbox.Label>
					</Checkbox.Root>
				))}
			</Flex>
			{rows.length === 0 ? (
				<Text mt={4} color="red.500">
					{t('general.nothing_to_export')}
				</Text>
			) : null}
			<Flex gap={2} marginTop={4}>
				<Button variant="outline" onClick={() => ref.current?.close()}>
					{t('general.cancel')}
				</Button>
				<Button disabled={columns.length === 0 || rows.length === 0} onClick={handleExport}>
					{t('general.export')}
				</Button>
			</Flex>
		</Flex>
	);
}
